import { AirForm } from '@/components/forms/auto-save-form';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogTitle, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/hooks/use-toast';
import { Label } from '@/components/ui/label';
import {
  createCoachVendor,
  getCoachVendor,
  getCoachVendorPossibleMatch,
  getMeCoach,
  syncCoachVendor,
  updateCoachVendor,
} from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import {
  accountClassEnum,
  accountClassToLabel,
  accountTypeEnum,
  accountTypeToLabel,
  createVendorSchema,
  getCoachACHPaymentOrNull,
} from '@airreading/common/data';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoaderIcon } from 'lucide-react';
import { useState } from 'react';
import { DefaultValues, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = createVendorSchema;

type FormSchema = z.infer<typeof formSchema>;
interface CoachPaymentSyncFormProps {
  type: 'create' | 'update';
  onClose?: () => void;
  defaultValues?: DefaultValues<FormSchema>;
}

export const CoachPaymentSyncForm = (props: CoachPaymentSyncFormProps) => {
  const { type, onClose, defaultValues } = props;

  const [isOpen, setIsOpen] = useState(true);

  const { mutateAsync: createVendor } = useMutation({
    mutationFn: createCoachVendor,
  });
  const { mutateAsync: updateVendor } = useMutation({
    mutationFn: updateCoachVendor,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const onSubmit = async (data: FormSchema) => {
    try {
      if (type === 'create') {
        await createVendor({
          ...data,
        });
        toast({
          description: 'Your bank account has been successfully linked',
        });
      }
      if (type === 'update') {
        await updateVendor({
          ...data,
        });
        toast({
          description: 'Your bank account has been successfully updated',
        });
      }
      queryClient.invalidateQueries(getMeCoach());
      queryClient.invalidateQueries(getCoachVendor());
      onClose?.();
    } catch (error) {
      console.error(error);
      const errorMessage =
        airServerErrorFrom(error)?.userFacingMessage ??
        (error instanceof Error
          ? error.message
          : 'Failed to link to bank account. Please check your routing number,account number, and try again');

      toast({
        variant: 'destructive',
        description: errorMessage,
      });
    }
  };

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...defaultValues,
      type: 'ACH',
    },
  });

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(value) => {
        setIsOpen(value);
        if (!value && onClose) {
          onClose?.();
        }
      }}
    >
      <DialogContent>
        <DialogTitle>{type === 'create' ? 'Add direct deposit info' : 'Update direct deposit info'}</DialogTitle>
        <p className="text-[#606060]">
          To receive payments internationally, please set up a Wise account at wise.com, then enter your Wise US routing
          and account numbers here as an ACH payment method.
        </p>
        <AirForm form={form}>
          <form onSubmit={form.handleSubmit((e) => onSubmit(e))}>
            <>
              <FormInputField form={form} label="Bank account holder name" name="companyName" />
              <FormSingleSelect
                form={form}
                label="Account type"
                name="accountType"
                options={accountTypeEnum.options}
                renderOption={(type) => accountTypeToLabel[type]}
              />
              <FormSingleSelect
                form={form}
                label="Account class"
                name="accountClass"
                options={accountClassEnum.options}
                renderOption={(type) => accountClassToLabel[type]}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormInputField
                  form={form}
                  label="ACH routing number"
                  name="routingNumber"
                  inputProps={{
                    type: 'number',
                  }}
                />
                <FormInputField
                  form={form}
                  label="ACH account number"
                  name="accountNumber"
                  inputProps={{
                    type: 'number',
                  }}
                />
              </div>
            </>
            <div className="flex gap-2 ml-auto items-center w-fit">
              <Button type="button" variant={'secondary'} onClick={() => onClose?.()}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={form.formState.isSubmitting}
                disabled={!form.formState.isValid || form.formState.isSubmitting}
              >
                Confirm
              </Button>
            </div>
          </form>
        </AirForm>
      </DialogContent>
    </Dialog>
  );
};

interface CoachPaymentNotSyncedProps {
  onClose: () => void;
  onOpen?: () => void;
}

export const CoachPaymentNotSynced = (props: CoachPaymentNotSyncedProps) => {
  const { onClose, onOpen } = props;

  const { data: possibleVendor, isLoading } = useQuery({ ...getCoachVendorPossibleMatch() });

  const { mutateAsync: syncVendor } = useMutation({
    mutationFn: syncCoachVendor,
  });
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [isContactUsOpen, setIsContactUsOpen] = useState(false);
  const [formOptions, setFormOptions] = useState<CoachPaymentSyncFormProps | undefined>();

  const onOpenSyncForm = async (type: 'contactUs' | 'connectAndUpdate' | 'noEmailFound') => {
    if (type === 'contactUs' || type === 'noEmailFound') {
      setIsContactUsOpen(false);
      setFormOptions({
        type: 'create',
        onClose: () => {
          setFormOptions(undefined);
          onClose();
        },
      });
    }
    if (type === 'connectAndUpdate') {
      await onConnectVendor(false);
    }
  };

  const onConnectVendor = async (shouldClose?: boolean) => {
    if (!possibleVendor?.id) return;

    try {
      await syncVendor({
        vendorId: possibleVendor.id,
        paymentInstrumentId: possibleVendor?.payment_accounts?.details.payment_instrument_id,
      });
      toast({
        description: 'Your bank account has been successfully linked!',
      });
      queryClient.invalidateQueries(getMeCoach());
    } catch (error) {
      console.error('Failed to sync vendor:', error);
      toast({
        title: 'Error',
        description: 'Failed to connect your payment method. Please try again.',
        variant: 'destructive',
      });
    } finally {
      if (shouldClose) {
        onClose?.();
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex gap-2 items-center">
        <LoaderIcon />
        <p className="font-semibold">connecting</p>
      </div>
    );
  }

  if (!possibleVendor) {
    return (
      <div>
        <p className="text-slate-400 mb-2" data-testid="profile-payment-modal-no-account-message">
          No account found with this email address. Please set up your bank account.
        </p>
        <div className="flex gap-2 items-center ml-auto w-fit">
          <DialogClose asChild>
            <Button variant={'secondary'} data-testid="profile-payment-modal-no-account-button-cancel">
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={() => onOpenSyncForm('noEmailFound')}
            data-testid="profile-payment-modal-no-account-button-create"
          >
            Create new account
          </Button>
          {formOptions && <CoachPaymentSyncForm {...formOptions} />}
        </div>
      </div>
    );
  }

  const payment = getCoachACHPaymentOrNull(possibleVendor);

  return (
    <>
      <DialogTitle>Bank Account</DialogTitle>
      <div className="space-y-8">
        <div className="grid space-y-3">
          <Label className="font-bold">Account Holder Full Name</Label>
          <Label className="text-slate-400">{possibleVendor?.company_name}</Label>
        </div>
        <div className="grid space-y-3">
          <Label className="font-bold">Bank routing number</Label>
          <Label className="text-slate-400">{payment?.payment_accounts.details.routing_number}</Label>
        </div>
        <div className="grid space-y-3">
          <Label className="font-bold">Account number</Label>
          <Label className="text-slate-400">{payment?.payment_accounts.details.account_number}</Label>
        </div>
      </div>
      <Dialog open={isContactUsOpen} onOpenChange={setIsContactUsOpen}>
        <DialogTrigger asChild>
          <Button variant={'link'} className="text-blue-500 underline hover:text-blue-600 mr-auto pl-0">
            Isn't this your account?
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogTitle />
          <p className="text-slate-400">
            If it's not your account, please{' '}
            <a
              className="text-blue-500 underline hover:text-blue-600 mr-auto pl-0 pr-0"
              href="https://help.airreading.com/support/home"
              target="_blank"
              rel="noopener noreferrer"
            >
              contact us
            </a>{' '}
            for help, or you can create a new account.
          </p>
          <div className="flex items-center gap-2 ml-auto">
            <DialogClose asChild>
              <Button variant={'secondary'}>Cancel</Button>
            </DialogClose>
            <Button onClick={() => onOpenSyncForm('contactUs')}>Create new account</Button>
          </div>
        </DialogContent>
      </Dialog>
      {formOptions && <CoachPaymentSyncForm {...formOptions} />}
      <div className="flex items-center justify-between">
        <Button
          variant={'secondary'}
          onClick={async () => {
            await onOpenSyncForm('connectAndUpdate');
            onOpen?.();
          }}
        >
          Connect and Update
        </Button>
        <div className="flex items-center gap-2">
          <DialogClose asChild>
            <Button variant={'secondary'}>Cancel</Button>
          </DialogClose>
          <Button onClick={() => onConnectVendor(true)}>Connect</Button>
        </div>
      </div>
    </>
  );
};
